import { useCallback, useEffect, useRef, useState } from 'react';
import comms from '../../comms';
import { sendGetFlagTripRates, sendQuoteFlagTrip } from '../Itinerary/itinerarySocketHelper';

/**
 * Shared rate / quote / pre-trip-payment state machine for flag trips, used by both
 * LiveDispatch's FlagTripModal and Itinerary's FlagTripCreateModal (see
 * docs/DriverRequirement/03_flag_trip_client_handoff.md: "Build one reusable
 * rate/destination/quote/hosted-payment component and use it from the existing live
 * and itinerary flag-trip screens"). Both modes fetch rates and request quotes over
 * the same ITINERARY_ACTION channel (`getFlagTripRates`/`quoteFlagTrip`) — live mode
 * just adds `mode:'live'` to bypass the itinerary-only feature gate server-side.
 * Trip *creation* itself differs per mode (FLAGM vs createItineraryFlagTrip) and is
 * NOT handled here — callers build their own submit payload from `quote.quoteToken`
 * and (if applicable) `prepay.prepayRef`.
 *
 * NOTE: as of this writing, no deployed fleet config sets flagTripPolicy.enabled, so
 * the server does not yet actually enforce destinationRequired/paymentTiming/quoteToken
 * — but every rate/quote response already carries those fields for real, and this hook
 * uses them, so behavior upgrades automatically once a fleet turns policy on.
 */
export function useFlagTripQuote({ isOpen, mode, vehicleId, originLat, originLon }) {
  const [rates, setRates] = useState([]);
  const [ratesError, setRatesError] = useState(null);
  const [selectedRateId, setSelectedRateId] = useState('');

  const [quote, setQuote] = useState(null); // full flagTripQuote payload, or null
  const [isQuoting, setIsQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  // prepay: { status: 'idle'|'pending'|'settled'|'failed', url, prepayRef, error }
  const [prepay, setPrepay] = useState({ status: 'idle', url: null, prepayRef: null, error: null });

  const quoteInputRef = useRef(null); // the exact fields the last successful quote used

  useEffect(() => {
    if (!isOpen) return undefined;

    setRates([]);
    setRatesError(null);
    setSelectedRateId('');
    setQuote(null);
    setQuoteError(null);
    setPrepay({ status: 'idle', url: null, prepayRef: null, error: null });
    quoteInputRef.current = null;

    const socket = comms.getSocket();
    if (!socket) {
      setRatesError('Not connected to dispatch. Try again in a moment.');
      return undefined;
    }

    const onRates = (r) => {
      if (r?.success) {
        const list = Array.isArray(r.rates) ? r.rates : [];
        setRates(list);
        // A flag trip (unscheduled street hail) should default straight to whichever
        // rate is actually the "Flag Trip" rate rather than making the driver pick it
        // out of the fleet's full active-rate list by hand — the server doesn't narrow
        // this list itself yet (see the module doc comments), so the client defaults it.
        // Doesn't override an explicit choice the driver already made.
        setSelectedRateId((prev) => {
          if (prev) return prev;
          const flagRate = list.find(
            (x) => /flag/i.test(x.name || '') || /flag/i.test(x.code || '')
          );
          return flagRate ? flagRate.id : prev;
        });
      } else {
        setRatesError(r?.error || 'Could not load rates.');
      }
    };
    const onQuote = (q) => {
      setIsQuoting(false);
      if (q?.success) {
        setQuote(q);
        setQuoteError(null);
        // Recover an already-started (or already-settled) prepayment matching this exact
        // quote fingerprint — covers a reconnect/remount that wiped local prepay state
        // while the passenger was still completing the hosted page for the same trip
        // parameters (rateId + passengerCount + origin + destination + mileage). The
        // server looks this up by fingerprint, not by a specific token, so a brand-new
        // quote for the same inputs still finds the original collection.
        if (q.paymentTiming === 'before_submit' && quoteInputRef.current) {
          comms.getRecoverableFlagTripPrepayment(
            { vehicleId, quoteToken: q.quoteToken, quoteInput: quoteInputRef.current },
            (result) => {
              if (!result?.success || !result.prepayRef || !result.status) return;
              if (result.status === 'Settled') {
                setPrepay({ status: 'settled', url: result.url || null, prepayRef: result.prepayRef, error: null });
              } else if (result.status === 'Pending' || result.status === 'Created') {
                setPrepay({ status: 'pending', url: result.url || null, prepayRef: result.prepayRef, error: null });
              }
              // Failed/Expired: leave at 'idle' so the driver can request a fresh prepay.
            }
          );
        }
      } else {
        setQuote(q || null); // some failure branches still carry needsDestination/code, keep them
        setQuoteError(q?.error || 'Could not price this trip.');
      }
    };
    const onPrepayUrl = (p) => {
      if (!p?.prepayRef) return;
      setPrepay({ status: 'pending', url: p.url, prepayRef: p.prepayRef, error: null });
    };
    // Route by prepayRef and require an explicit 'Settled' status — a bare
    // `success:true` can arrive from getFlagTripPrepayment while status is still
    // 'Pending', and a stale/late result for a previously-abandoned prepay on the
    // same socket must not flip the CURRENT quote to settled.
    const onPrepayResult = (p) => {
      if (!p?.prepayRef) return;
      setPrepay((prev) => {
        if (prev.prepayRef && p.prepayRef !== prev.prepayRef) return prev;
        if (p.status === 'Settled') {
          return { ...prev, status: 'settled', prepayRef: p.prepayRef, error: null };
        }
        if (p.success === false || p.status === 'Failed' || p.status === 'Expired') {
          return { ...prev, status: 'failed', prepayRef: p.prepayRef, error: p.error || `Payment ${p.status || 'failed'}.` };
        }
        // Still Pending/Created — no change; keep waiting.
        return prev;
      });
    };

    socket.on('flagTripRates', onRates);
    socket.on('flagTripQuote', onQuote);
    socket.on('flagTripPrepayUrl', onPrepayUrl);
    socket.on('flagTripPrepayResult', onPrepayResult);

    sendGetFlagTripRates({ vehicleId, originLat, originLon, ...(mode === 'live' ? { mode: 'live' } : {}) });

    return () => {
      socket.off('flagTripRates', onRates);
      socket.off('flagTripQuote', onQuote);
      socket.off('flagTripPrepayUrl', onPrepayUrl);
      socket.off('flagTripPrepayResult', onPrepayResult);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Any change to rate/destination/passenger-count/mileage invalidates the current quote and
  // any in-flight/settled prepayment — per doc: "Any change ... invalidates the quote and
  // prepayment UI state."
  const invalidateQuote = useCallback(() => {
    setQuote(null);
    setQuoteError(null);
    quoteInputRef.current = null;
    setPrepay({ status: 'idle', url: null, prepayRef: null, error: null });
  }, []);

  const requestQuote = useCallback(
    (fields) => {
      if (!selectedRateId) return;
      setIsQuoting(true);
      setQuoteError(null);
      quoteInputRef.current = { rateId: selectedRateId, ...fields };
      sendQuoteFlagTrip({
        rateId: selectedRateId,
        ...fields,
        ...(mode === 'live' ? { mode: 'live' } : {}),
      });
    },
    [selectedRateId, mode]
  );

  const requestPrepay = useCallback(
    ({ driverId, routeId, presentation = 'embedded' } = {}) => {
      if (!quote?.quoteToken || !quoteInputRef.current) return false;
      setPrepay({ status: 'pending', url: null, prepayRef: null, error: null });
      const ok = comms.requestFlagTripPrepayment({
        vehicleId,
        driverId,
        routeId,
        quoteToken: quote.quoteToken,
        quoteInput: quoteInputRef.current,
        presentation,
      });
      if (!ok) {
        setPrepay({ status: 'failed', url: null, prepayRef: null, error: 'Not connected to dispatch.' });
      }
      return ok;
    },
    [quote, vehicleId]
  );

  // Manual refresh for when the async flagTripPrepayResult webhook never arrives
  // (socket blip, backgrounded PWA, suspended tab) — lets the driver un-stick a
  // paid-but-not-yet-reflected prepay instead of Submit staying disabled forever.
  const [isCheckingPrepay, setIsCheckingPrepay] = useState(false);
  const checkPrepayStatus = useCallback(() => {
    if (!prepay.prepayRef) return;
    setIsCheckingPrepay(true);
    comms.getFlagTripPrepayment({ prepayRef: prepay.prepayRef }, (result) => {
      setIsCheckingPrepay(false);
      if (!result?.prepayRef) return;
      setPrepay((prev) => {
        if (prev.prepayRef && result.prepayRef !== prev.prepayRef) return prev;
        if (result.status === 'Settled') {
          return { ...prev, status: 'settled', prepayRef: result.prepayRef, error: null };
        }
        if (result.success === false || result.status === 'Failed' || result.status === 'Expired') {
          return { ...prev, status: 'failed', prepayRef: result.prepayRef, error: result.error || `Payment ${result.status || 'failed'}.` };
        }
        return prev; // still Pending — no change
      });
    });
  }, [prepay.prepayRef]);

  const selectedRate = rates.find((r) => r.id === selectedRateId) || null;

  return {
    rates,
    ratesError,
    selectedRateId,
    setSelectedRateId,
    selectedRate,
    quote,
    isQuoting,
    quoteError,
    requestQuote,
    invalidateQuote,
    prepay,
    requestPrepay,
    checkPrepayStatus,
    isCheckingPrepay,
  };
}
